import * as XLSX from 'xlsx';

type ExportCell = string | number | boolean | Date | null | undefined;

export interface ExcelExportOptions {
  filename: string;
  sheetName?: string;
  headers: string[];
  data: ExportCell[][];
  additionalInfo?: { label: string; value: string | number }[];
}

/**
 * Универсальная функция для экспорта данных в Excel
 */
export interface ExcelMultiSheetOptions {
  filename: string;
  sheets: { name: string; rows: ExportCell[][] }[];
}

export interface CsvExportOptions {
  filename: string;
  headers: string[];
  data: ExportCell[][];
  separator?: string;
}

const BOM = '\uFEFF';

const normalizeExportCell = (cell: ExportCell): string | number | boolean => {
  if (cell === null || cell === undefined) {
    return '';
  }

  if (cell instanceof Date) {
    return cell.toLocaleString('ru-RU');
  }

  if (typeof cell === 'string') {
    return cell.replace(/\r\n|\r|\n/g, ' ');
  }

  return cell;
};

const isEmptyExportRow = (row: ExportCell[]): boolean => {
  return row.every((cell) => String(normalizeExportCell(cell)).trim() === '');
};

const normalizeRowsToWidth = (rows: ExportCell[][], width: number, removeEmptyRows = false): any[][] => {
  return rows
    .filter((row) => !removeEmptyRows || !isEmptyExportRow(row))
    .map((row) => Array.from({ length: width }, (_, index) => normalizeExportCell(row[index])));
};

const getMaxRowWidth = (rows: ExportCell[][]): number => {
  return rows.reduce((maxWidth, row) => Math.max(maxWidth, row.length), 0);
};

const getColumnWidths = (rows: any[][], width: number): XLSX.ColInfo[] => {
  return Array.from({ length: width }, (_, index) => {
    const maxLength = rows.reduce((max, row) => {
      const cell = row[index];
      const length = cell === null || cell === undefined ? 0 : String(cell).length;
      return Math.max(max, length);
    }, 0);

    return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
  });
};

const safeSheetName = (name: string): string => {
  return name.replace(/[[\]:*?/\\]/g, '_').substring(0, 31) || 'Sheet';
};

export const escapeCsvCell = (cell: ExportCell): string => {
  return `"${String(normalizeExportCell(cell)).replace(/"/g, '""')}"`;
};

export const buildCsvContent = (headers: string[], data: ExportCell[][], separator = ';'): string => {
  const normalizedHeaders = headers.map((header) => normalizeExportCell(header));
  const normalizedData = normalizeRowsToWidth(data, headers.length, true);

  return [normalizedHeaders, ...normalizedData]
    .map((row) => row.map((cell) => escapeCsvCell(cell)).join(separator))
    .join('\n');
};

export const downloadCsvContent = (filename: string, content: string): void => {
  const blob = new Blob([BOM + content], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const exportToCsv = (options: CsvExportOptions): void => {
  const { filename, headers, data, separator = ';' } = options;
  downloadCsvContent(filename, buildCsvContent(headers, data, separator));
};

/** Несколько листов в одной книге (например: «Сводка» + детализация). */
export const exportToExcelMultiSheet = (options: ExcelMultiSheetOptions): void => {
  try {
    const { filename, sheets } = options;
    const workbook = XLSX.utils.book_new();

    for (const sheet of sheets) {
      const safeName = safeSheetName(sheet.name);
      const width = getMaxRowWidth(sheet.rows);
      const normalizedRows = normalizeRowsToWidth(sheet.rows, width);
      const worksheet = XLSX.utils.aoa_to_sheet(normalizedRows);
      worksheet['!cols'] = getColumnWidths(normalizedRows, width);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
    }

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Ошибка экспорта в Excel:', error);
    alert('Произошла ошибка при экспорте в Excel');
  }
};

export const exportToExcel = (options: ExcelExportOptions): void => {
  try {
    const { filename, sheetName = 'Данные', headers, data, additionalInfo } = options;

    // Создаем рабочую книгу
    const workbook = XLSX.utils.book_new();

    // Подготавливаем данные для листа
    const worksheetData: any[][] = [];
    const width = headers.length;

    // Добавляем заголовки
    worksheetData.push(headers.map((header) => normalizeExportCell(header)));

    // Добавляем основные данные
    worksheetData.push(...normalizeRowsToWidth(data, width, true));

    // Добавляем дополнительную информацию (если есть)
    if (additionalInfo && additionalInfo.length > 0) {
      worksheetData.push(Array(width).fill('')); // Пустая строка
      additionalInfo.forEach(info => {
        const infoRow = Array(width).fill('');
        infoRow[0] = normalizeExportCell(info.label);
        infoRow[1] = normalizeExportCell(info.value);
        worksheetData.push(infoRow);
      });
    }

    // Создаем лист из данных
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Настраиваем ширину колонок
    const columnWidths = getColumnWidths(worksheetData, width);

    worksheet['!cols'] = columnWidths;

    // Добавляем лист в рабочую книгу
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName(sheetName));

    // Генерируем файл и скачиваем
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`Excel файл "${filename}.xlsx" успешно экспортирован`);
  } catch (error) {
    console.error('Ошибка экспорта в Excel:', error);
    alert('Произошла ошибка при экспорте в Excel');
  }
};

/**
 * Форматирует дату для использования в Excel
 */
export const formatDateForExcel = (date: Date): string => {
  return date.toLocaleDateString('ru-RU');
};

/**
 * Форматирует дату и время для использования в Excel
 */
export const formatDateTimeForExcel = (date: Date): string => {
  return date.toLocaleString('ru-RU');
};
